// Shared helper for avatar-fallback initials and the stable brand-friendly
// background color we pick from the user's identity.
//
// Tailwind JIT will see every class string literal below, so the `bg-...`
// values stay compiled into the final CSS even though we return them
// dynamically from `avatarColor()`.

export function initialsFromName(name) {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function initials(user) {
  if (!user) return '';
  const fn = (user.first_name || '').trim();
  const ln = (user.last_name || '').trim();
  if (fn || ln) {
    return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase() || initialsFromName(user.name);
  }
  return initialsFromName(user.name);
}

// ─── Avatar color palette ────────────────────────────────────────────────
// Hand-picked to stay in Neighborli's warm, earthy family: deep foliage,
// terracotta, slate, amber, plum. Every entry pairs a bg class with the text
// color that gives WCAG-AA contrast against it. These literals must appear
// as whole strings so the Tailwind scanner emits them.
const PALETTE = [
  { bg: 'bg-emerald-800',  text: 'text-emerald-50' },
  { bg: 'bg-green-700',    text: 'text-green-50'   },
  { bg: 'bg-teal-700',     text: 'text-teal-50'    },
  { bg: 'bg-cyan-800',     text: 'text-cyan-50'    },
  { bg: 'bg-sky-800',      text: 'text-sky-50'     },
  { bg: 'bg-indigo-700',   text: 'text-indigo-50'  },
  { bg: 'bg-purple-700',   text: 'text-purple-50'  },
  { bg: 'bg-fuchsia-700',  text: 'text-fuchsia-50' },
  { bg: 'bg-rose-700',     text: 'text-rose-50'    },
  { bg: 'bg-red-700',      text: 'text-red-50'     },
  { bg: 'bg-orange-700',   text: 'text-orange-50'  },
  { bg: 'bg-amber-700',    text: 'text-amber-50'   },
  { bg: 'bg-yellow-700',   text: 'text-yellow-50'  },
  { bg: 'bg-lime-700',     text: 'text-lime-50'    },
  { bg: 'bg-stone-700',    text: 'text-stone-50'   },
  { bg: 'bg-slate-700',    text: 'text-slate-50'   },
];

// Deterministic 32-bit string hash (djb2 variant). Same seed → same bucket,
// so a user's avatar stays the same color across sessions, devices, and
// rerenders — no server round-trip needed.
function hashString(seed) {
  let h = 5381;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

/**
 * Pick a stable bg/text class pair for the given user. Falls back to the
 * brand-primary green when no identity data is available (so a totally
 * anonymous avatar still looks like it belongs to the app).
 *
 * @param {object|string|null|undefined} userOrSeed
 *   A user doc ({id, first_name, last_name, name, email}) or a raw seed
 *   string (useful for things like `post.author_name`).
 */
export function avatarColor(userOrSeed) {
  if (!userOrSeed) return { bg: 'bg-[#004B00]', text: 'text-white' };
  let seed;
  if (typeof userOrSeed === 'string') {
    seed = userOrSeed;
  } else {
    // Prefer the immutable id — guarantees stability if the user renames.
    seed = userOrSeed.id || userOrSeed.email || userOrSeed.name
      || `${userOrSeed.first_name || ''} ${userOrSeed.last_name || ''}`;
  }
  if (!seed || !String(seed).trim()) {
    return { bg: 'bg-[#004B00]', text: 'text-white' };
  }
  return PALETTE[hashString(seed) % PALETTE.length];
}
